import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import styles from "./CeldaDeChips.module.scss";

/**
 * Celda para una columna que lleva VARIOS valores cortos: los muestra como chips,
 * los que entran enteros, y el resto detrás de un "+N" que los lista al pasar el
 * mouse.
 *
 * La usan la columna "Rangos horarios" de Horarios y la de "Funciones" de
 * Agrupamientos. Son el mismo problema —una lista dentro de una fila de 36px— y
 * conviene que se vean y se operen igual.
 *
 * RECIBE ETIQUETAS YA ARMADAS, strings. El formato lo resuelve el loader de cada
 * vista: así esta celda no sabe nada de horas ni de nombres de función, y sirve para
 * la próxima columna que necesite lo mismo.
 *
 * MUESTRA LAS QUE ENTRAN ENTERAS Y NI UNA MÁS. No hay un tope fijo de chips: se MIDE
 * cuántos caben en el ancho real de la celda. Un número fijo no sirve cuando los
 * valores miden distinto —"Mucama" contra "Atención Primaria para el Área"—, porque
 * en la misma columna entran cuatro o entra uno.
 *
 * CÓMO SE MIDE, Y POR QUÉ CON UNA COPIA OCULTA
 *
 * Medir sobre los chips visibles se muerde la cola: al ocultar los que sobran cambia
 * el ancho ocupado, la siguiente medición da otro resultado y el cálculo oscila. Por
 * eso existe `medidor`: una copia con TODOS los chips —más uno de muestra del "+N"—
 * que no se ve, no ocupa lugar y no cambia nunca. De ahí salen los anchos; la decisión
 * se aplica sobre la lista visible.
 *
 * Se vuelve a medir cuando cambia el ancho de la celda: las columnas son
 * redimensionables, así que un cálculo hecho una sola vez al montar queda viejo apenas
 * el usuario arrastra el borde.
 *
 * @param items  las etiquetas, ya formateadas.
 * @param vacio  qué decir cuando no hay ninguna.
 */

// Tiene que coincidir con el `gap` de `.contenedor` en el SCSS.
const GAP = 6;

// Un ancho que no se va a quedar corto para el "+N" real.
const MUESTRA_PILL = "+99";

export default function CeldaDeChips({ items, vacio = "Sin datos" }) {
    // `useMemo` y no una derivacion suelta: sin el, cada render crea un array nuevo,
    // el efecto lo ve como una dependencia distinta y reinstala el ResizeObserver en
    // cada pasada. La grilla tiene muchas celdas; eso se multiplica.
    const etiquetas = useMemo(
        () => (Array.isArray(items) ? items.filter(Boolean) : []),
        [items]
    );

    const contenedorRef = useRef(null);
    const medidorRef = useRef(null);
    // Arranca mostrando todas: si entran, la medición no cambia nada y no parpadea.
    const [corte, setCorte] = useState(etiquetas.length);

    useLayoutEffect(() => {
        const contenedor = contenedorRef.current;
        const medidor = medidorRef.current;
        if (!contenedor || !medidor) return;

        const medir = () => {
            const nodos = Array.from(medidor.children);
            if (nodos.length === 0) return;

            // El último hijo del medidor es la muestra del "+N".
            const anchoPill = nodos[nodos.length - 1].offsetWidth;
            const anchos = nodos.slice(0, -1).map((n) => n.offsetWidth);
            const disponible = contenedor.clientWidth;

            const totalTodas = anchos.reduce(
                (suma, ancho, i) => suma + ancho + (i > 0 ? GAP : 0),
                0
            );
            if (totalTodas <= disponible) {
                setCorte(anchos.length);
                return;
            }

            // No entran todas, así que hay que reservarle lugar al "+N".
            const limite = disponible - anchoPill - GAP;
            let acumulado = 0;
            let cuantas = 0;
            for (let i = 0; i < anchos.length; i++) {
                const ancho = anchos[i] + (i > 0 ? GAP : 0);
                if (acumulado + ancho > limite) break;
                acumulado += ancho;
                cuantas++;
            }
            // Puede dar 0: un solo valor largo que no entra ni solo. Ahí la celda
            // queda con el "+N" nada más, que es honesto y se puede consultar.
            setCorte(cuantas);
        };

        medir();
        const observador = new ResizeObserver(medir);
        observador.observe(contenedor);
        return () => observador.disconnect();
    }, [etiquetas]);

    // Sin clase, así hereda la tipografía de la fila como cualquier otro texto de la
    // grilla: una lista vacía es un dato del registro, no una ausencia que atenuar.
    if (etiquetas.length === 0) {
        return <span>{vacio}</span>;
    }

    const visibles = etiquetas.slice(0, corte);
    const ocultas = etiquetas.slice(corte);

    return (
        <div className={styles.contenedor} ref={contenedorRef}>
            {visibles.map((etiqueta, i) => (
                <span key={i} className={styles.chip}>
                    {etiqueta}
                </span>
            ))}

            {ocultas.length > 0 && (
                <Tooltip
                    // Una por línea: son valores sueltos, no una enumeración. En
                    // renglón corrido con comas hay que leerlos enteros para separarlos.
                    title={
                        <span className={styles.lista}>
                            {ocultas.map((etiqueta, i) => (
                                <span key={i}>{etiqueta}</span>
                            ))}
                        </span>
                    }
                    enterDelay={200}
                >
                    <span className={styles.resto}>+{ocultas.length}</span>
                </Tooltip>
            )}

            {/* La copia que se mide. Fuera del flujo y sin pintar: existe para que
                `offsetWidth` tenga de dónde salir sin que lo que se ve la afecte. */}
            <div className={styles.medidor} ref={medidorRef} aria-hidden="true">
                {etiquetas.map((etiqueta, i) => (
                    <span key={i} className={styles.chip}>
                        {etiqueta}
                    </span>
                ))}
                <span className={styles.resto}>{MUESTRA_PILL}</span>
            </div>
        </div>
    );
}
